import { useState, useEffect, useCallback, useRef } from 'react';
import { Paper, TextField, Box, IconButton, Chip, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { formatDate } from '../../../utils/DateUtils';
import PageHeader from '../../../components/PageHeader/PageHeader';
import TableRefreshButton from '../../../components/TableRefreshButton/TableRefreshButton';
import ViewBillingStatementModal from './ViewBillingStatementModal';
import PDFViewerModal from '../orders/PDFViewerModal';
import SendBillingStatementEmailModal from './SendBillingStatementEmailModal';
import AccountMovementsModal from '../../../components/AccountMovementsModal/AccountMovementsModal';
import { BillingStatementService } from '../../../services/B2BServices';
import B2BUtilsService from '../../../services/B2BUtilsService';
import dayjs from 'dayjs';
import { formatMoney } from '../../../utils/currency';

const BillingStatement = () => {
  const { t } = useTranslation();

  const [statements, setStatements] = useState([]);
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);
  const [isSendEmailModalOpen, setIsSendEmailModalOpen] = useState(false);
  const [movementsStatement, setMovementsStatement] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [searchTerm, setSearchTerm] = useState('');
  const [totalRows, setTotalRows] = useState(0);
  const searchTimeoutRef = useRef(null);

  const fetchStatements = useCallback(async (
    page,
    pageSize,
    search,
    currentSortModel = sortModel,
    currentFilterModel = filterModel
  ) => {
    try {
      setLoading(true);
      const activeSort = Array.isArray(currentSortModel) ? currentSortModel[0] : null;
      const activeFilter = currentFilterModel?.items?.find((item) => item?.field && item?.operator);
      const response = await BillingStatementService.getBillingStatements(page, pageSize, search, {
        sortField: activeSort?.field,
        sortDirection: activeSort?.sort,
        filterField: activeFilter?.field,
        filterOperator: activeFilter?.operator,
        filterValue: activeFilter?.value
      });

      const statementsData = response?.data?.statements || response?.statements || [];
      const totalCount = response?.data?.totalCount || response?.totalCount || 0;
      setStatements(statementsData);
      setTotalRows(totalCount);
    } catch (error) {
      console.error('❌ BillingStatement - Error fetching billing statements:', error);
      setStatements([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [sortModel, filterModel]);

  useEffect(() => {
    fetchStatements(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [fetchStatements, paginationModel.page, paginationModel.pageSize, sortModel, filterModel]);

  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterModelChange = (newFilterModel) => {
    const normalizedItems = (newFilterModel?.items || [])
      .filter((item) => item?.field && item?.operator)
      .slice(0, 1);

    setFilterModel({ ...newFilterModel, items: normalizedItems });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSearchChange = useCallback((newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    const currentPageSize = paginationModel.pageSize;
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchStatements(1, currentPageSize, newSearchTerm, sortModel, filterModel);
    }, 500);
  }, [paginationModel.pageSize, sortModel, filterModel, fetchStatements]);

  const handleViewStatement = async (statement) => {
    try {
      setDetailsLoading(true);
      const detailResponse = await BillingStatementService.getBillingStatementById(statement.id || statement.clientId);
      const detail = detailResponse?.data || detailResponse;
      const clientName = detail?.clientName || detail?.client?.nombreFantasia || detail?.client?.razonSocial || statement.clientName;

      setSelectedStatement({
        ...statement,
        ...detail,
        clientId: detail?.clientId || detail?.client?.id || statement.clientId,
        clientName
      });
      setIsViewModalOpen(true);
    } catch (error) {
      console.error('❌ BillingStatement - Error fetching billing statement detail:', error);
      setSelectedStatement(statement);
      setIsViewModalOpen(true);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleViewPDF = async (statement) => {
    try {
      console.log('🔍 BillingStatement - Generando PDF para statement:', statement.id);
      
      const pdfBlob = await BillingStatementService.generateStatementPDF(statement.id);
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
      setSelectedStatement(statement);
      setIsPDFModalOpen(true);
      
      console.log('✅ BillingStatement - PDF generado exitosamente');
    } catch (error) {
      console.error('❌ BillingStatement - Error generando PDF:', error);
    }
  };

  const handleClosePDFModal = () => {
    setIsPDFModalOpen(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const handleSendEmail = (statement) => {
    setSelectedStatement(statement);
    setIsSendEmailModalOpen(true);
  };

  const getCompanyStatusColor = (status) => {
    return B2BUtilsService.getCompanyStatusColor(status);
  };

  const getCompanyStatusText = (status) => {
    return B2BUtilsService.getCompanyStatusText(status, t);
  };

  const columns = [
    { 
      field: 'clientName', 
      headerName: t('client'), 
      flex: 1.5 
    },
    { 
      field: 'ordersCount', 
      headerName: t('orders_count'), 
      type: 'number',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'status',
      headerName: t('status'),
      flex: 1.3,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={getCompanyStatusText(params.value)}
          color={getCompanyStatusColor(params.value)}
          size="small"
        />
      )
    },
    { 
      field: 'totalAmount', 
      headerName: t('total_amount'), 
      type: 'number', 
      flex: 1, 
      align: 'center', 
      headerAlign: 'center',
      valueFormatter: (params) =>
        typeof params === 'number' ? formatMoney(params) : '—', 
    },
    { 
      field: 'paidAmount', 
      headerName: t('paid_amount'), 
      type: 'number', 
      flex: 1, 
      align: 'center', 
      headerAlign: 'center',
      valueFormatter: (params) =>
        typeof params === 'number' ? formatMoney(params) : '—', 
    },
    { 
      field: 'pendingAmount', 
      headerName: t('pending_amount'), 
      type: 'number', 
      flex: 1, 
      align: 'center', 
      headerAlign: 'center',
      valueFormatter: (params) =>
        typeof params === 'number' ? formatMoney(params) : '—', 
    },
    {
      field: 'dueDate',
      headerName: t('due_date'),
      flex: 1.2,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const date = dayjs(params.value);
        return (
          <Box sx={{ textAlign: 'center' }}>
            <div>{formatDate(date)}</div>
          </Box>
        );
      }
    },
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 1.5,
      minWidth: 200,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 0.5 }}>
          <Tooltip title="Ver detalle de pedidos">
            <IconButton onClick={() => handleViewStatement(params.row)} sx={{ width: 40, height: 40, flexShrink: 0 }}>
              {getIcon('eye')}
            </IconButton>
          </Tooltip>
          <Tooltip title="Ver movimientos">
            <IconButton onClick={() => setMovementsStatement(params.row)} sx={{ width: 40, height: 40, flexShrink: 0 }}>
              {getIcon('movements')}
            </IconButton>
          </Tooltip>
          <Tooltip title="Ver PDF de movimientos">
            <IconButton onClick={() => handleViewPDF(params.row)} sx={{ width: 40, height: 40, flexShrink: 0 }}>
              {getIcon('reader')}
            </IconButton>
          </Tooltip>
          <Tooltip title="Enviar por correo">
            <IconButton onClick={() => handleSendEmail(params.row)} color="primary" sx={{ width: 40, height: 40, flexShrink: 0 }}>
              {getIcon('mail')}
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box p={3}>
      <PageHeader title={t('billing_statements')} subtitle={t('manage_billing_statements')} />
      
      {/* Tabla de estados de cuenta */}
      <Paper sx={{ padding: 2 }}>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label={t('search')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{ width: 300 }}
          />
          <TableRefreshButton loading={loading} onClick={() => fetchStatements(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel)} />
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 1000 }}>
            <DataGrid
              loading={loading || detailsLoading}
              rows={statements}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              sortingMode="server"
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
              filterMode="server"
              paginationMode="server"
              rowCount={totalRows}
              pageSizeOptions={[10, 25, 50]}
              getRowId={(row) => row.id || row.clientId}
            />
          </Box>
        </Box>
      </Paper>
      
      <ViewBillingStatementModal
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        statement={selectedStatement}
      />
      
      <PDFViewerModal
        open={isPDFModalOpen}
        onClose={handleClosePDFModal}
        statement={selectedStatement}
        pdfUrl={pdfUrl}
      />
      
      <SendBillingStatementEmailModal
        open={isSendEmailModalOpen}
        onClose={() => setIsSendEmailModalOpen(false)}
        statement={selectedStatement}
      />

      <AccountMovementsModal
        open={Boolean(movementsStatement)}
        onClose={() => setMovementsStatement(null)}
        subject={movementsStatement}
        subjectName={movementsStatement?.clientName}
        fetchMovements={(page, pageSize) =>
          BillingStatementService.getClientMovements(movementsStatement.id || movementsStatement.clientId, page, pageSize)
        }
        methodLabels={{ CASH: 'Efectivo', TRANSFER: 'Transferencia', CARD: 'Tarjeta', CHECK: 'Cheque' }}
      />
    </Box>
  );
};

export default BillingStatement;
